import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../i18n/i18n';

interface Conversation {
  session_id: string;
  title: string;
  message_count: number;
  last_message_at: string;
}

interface Props {
  currentSessionId: string | null;
  onSelectConversation: (sessionId: string | null) => void;
}

export const ConversationSelector: React.FC<Props> = ({
  currentSessionId,
  onSelectConversation
}) => {
  const { t } = useI18n();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);

  const loadConversations = async () => {
    try {
      const response = await api.get<Conversation[]>('/api/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  const handleNewConversation = () => {
    onSelectConversation(null);
    loadConversations(); // Refresh list
  };

  const handleDeleteConversation = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(t('chat.confirmDelete'))) return;

    try {
      await api.delete(`/api/conversations/${sessionId}`);
      setConversations(prev => prev.filter(c => c.session_id !== sessionId));
      if (currentSessionId === sessionId) {
        onSelectConversation(null);
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  return (
    <div className="mb-4 border-b border-gray-200 pb-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700">
          {t('chat.conversations')}
        </h4>
        <button
          onClick={handleNewConversation}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          title={t('chat.newConversation')}
        >
          <Plus className="h-4 w-4 text-primary-600" />
        </button>
      </div>

      <div className="space-y-1 max-h-32 overflow-y-auto">
        {loading ? (
          <div className="text-xs text-gray-500">{t('chat.loading')}</div>
        ) : conversations.length === 0 ? (
          <div className="text-xs text-gray-500 italic">{t('chat.noConversations')}</div>
        ) : (
          conversations.map((conv) => (
            <div
              key={conv.session_id}
              className={`flex items-center justify-between p-2 rounded text-xs cursor-pointer transition-colors ${
                currentSessionId === conv.session_id
                  ? 'bg-primary-100 text-primary-700'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
              onClick={() => onSelectConversation(conv.session_id)}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <MessageSquare className="h-3 w-3 flex-shrink-0" />
                <span className="truncate font-medium">{conv.title}</span>
                <span className="text-gray-500 flex-shrink-0">({conv.message_count})</span>
              </div>
              <button
                onClick={(e) => handleDeleteConversation(conv.session_id, e)}
                className="p-1 hover:bg-red-100 rounded flex-shrink-0 transition-colors"
                title={t('chat.deleteConversation')}
              >
                <Trash2 className="h-3 w-3 text-red-600" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationSelector;
